import { Injectable, inject, resource, signal, computed } from "@angular/core";
import { IframeMessagesService } from "@shared/iframe-messages-service";
import { AnalyticsService } from "@shared/analytics-service/analytics.service";
import { CardModalService } from "@shared/card-modal";
import { TranslateService } from "@ngx-translate/core";
import { DatePipe } from "@angular/common";

export interface Note {
    id: number | string;
    userName: string;
    text: string;
    formattedDate?: string;
}

type ServiceId = string | undefined;

export type DateLike = string | number | Date | null | undefined;

@Injectable()
export abstract class BaseNotesService<S extends Note> {
    private readonly translateService = inject(TranslateService);
    protected iframeMessagesService = inject(IframeMessagesService);
    protected readonly analyticsService = inject(AnalyticsService);
    protected readonly cardModalService = inject(CardModalService);
    protected readonly datePipe = inject(DatePipe);

    public id = signal<ServiceId>(undefined);

    protected abstract dateField: keyof {
        [K in keyof S as S[K] extends DateLike ? K : never]: S[K];
    };

    protected abstract getNotes(id: string): Promise<S[]>;
    protected abstract addNote(id: string, text: string): Promise<S>;
    protected abstract deleteNote(id: string, noteId: string): Promise<{ success: boolean }>;
    protected abstract sortNote(a: S, b: S): number;

    private readonly language = computed(() => {
        return this.translateService.getCurrentLang() ? this.translateService.getCurrentLang() : this.translateService.getFallbackLang();
    });

    protected dateFormat = computed(() => {
        const lang = this.language();
        return lang === "en-CA" ? "yyyy-MM-dd 'at' h:mm a" : "yyyy-MM-dd 'à' HH:mm";
    });

    public async add(id: string, text: string): Promise<void> {
        try {
            const newNote = await this.addNote(id, text);
            if (!newNote) return;
            this.notesResource.update((notes) => [...notes, newNote]);
        } catch (error) {
            console.error(`Failed to add notes: ${error}`);
            throw error;
        }
    }

    public async delete(id: string, noteId: string): Promise<void> {
        try {
            await this.deleteNote(id, noteId);
            this.notesResource.update((notes) => notes.filter((n) => n.id.toString() !== noteId));
        } catch (error) {
            console.error(`Failed to delete notes: ${error}`);
            throw error;
        }
    }

    private readonly notesResource = resource<S[], ServiceId>({
        defaultValue: [],
        params: () => this.id(),
        loader: async ({ params: id }: { params: ServiceId }) => {
            if (!id) return [];
            try {
                const notes = await this.getNotes(id);
                return notes;
            } catch (err) {
                console.error(`Failed to load notes: ${err}`);
                return [];
            }
        },
    });

    public notes = computed(() => {
        const notes = this.notesResource.value();
        const dateFormat = this.dateFormat();

        for (const note of notes) {
            const rawDate = note[this.dateField];
            const normalized = typeof rawDate === "number" ? rawDate * 1000 : rawDate;
            const theDate = new Date(normalized as string | number);
            const formattedDate = this.datePipe.transform(theDate, dateFormat, undefined, "en-US") ?? "";
            note.formattedDate = formattedDate;
        }
        return notes.sort(this.sortNote);
    });
}
